import useTranslation from 'next-translate/useTranslation'
import { useDispatch } from 'react-redux'
import useTransaction from 'tennders-transactions/hooks/useTransaction'
import { cancelPermissions, deletePermissions, EXTERNAL_LOAD_DELETE } from 'tennders-stracture/libs/permissions'
import { STATUS_CANCELLED, STATUS_DELETED } from 'tennders-stracture/libs/constants'
import useSocket from 'tennders-common/hooks/useSocket'
import usePopup from 'tennders-common/hooks/usePopup'
import useSession from 'tennders-auth/hooks/useSession'
import useForm from 'tennders-common/form/hooks/useForm'
import RequiresPermission from 'tennders-auth/components/RequiresPermission'
import GHref from 'tennders-market/common/components/element/GHref'
import { ItemTypes } from 'tennders-common/enums/ItemTypes'
import { ApiRoutesNoSlash } from 'tennders-common/enums/ApiRoutesNoSlash'
import { TransactionType } from 'tennders-transactions/types/TransactionType'
import { dispatchDeleteModal } from 'tennders-common/stores/modal/action'
import useLoadsStore from 'tennders-market/load/stores/useLoadsStore'
import useTrucksStore from 'tennders-market/truck/stores/useTrucksStore'

type Props = {
            transaction: TransactionType,
            isDeleteButton?: boolean,
            type?: string;
            board?: number | string,
            closeTransaction?: () => void
};

const selector = state => state.deletePost

export const withDeleteTransaction = (WrappedComponent) => ({
  transaction,
  type,
  board,
  isDeleteButton,
  closeTransaction
}: Props) => {
  const { popConfirm } = usePopup()
  const { t } = useTranslation()
  const { isBroker } = useSession()
  const { setRefresh } = useSocket()
  const dispatch = useDispatch()
  const isCancelButton = (transaction?.matchedLoad && transaction?.matchedTruck) && transaction?.status?.label !== STATUS_CANCELLED && !isDeleteButton
  const { transaction: openedTransaction, refreshTransaction } = useTransaction()
  const deleteLoad = useLoadsStore(selector)
  const deleteTruck = useTrucksStore(selector)
  const close = () => dispatch(dispatchDeleteModal({ transaction: null, onDelete: null }))
  const setForDelete = (transaction: TransactionType) => {
    dispatch(dispatchDeleteModal({
      transaction,
      onDelete: () => {
        close()
        if (openedTransaction) {
          refreshTransaction()
        }
        setRefresh('all')
      }
    }))
  }

  const { handleDelete } = useForm({ initialForm: undefined })

  const deleteItem = async () => {
    if (type === ItemTypes.MATCHED && isBroker() && !transaction.isPassive) {
      setForDelete(transaction)
      return
    }
    let id = -1
    let url = ''
    if (type === ItemTypes.LOAD) {
      id = transaction?.matchedLoad?.id
      url = ApiRoutesNoSlash.loads
    }
    if (type === ItemTypes.TRUCK) {
      id = transaction?.matchedTruck?.id
      url = ApiRoutesNoSlash.trucks
    }
    await handleDelete({
      id,
      url,
      okMsg: t('tableRow:deletedMsg'),
      confirmTitle: t('tableRow:deleteItem'),
      confirmMessage: t('tableRow:deleteAsk'),
      next: (result) => {
        if (result) {
          if (type === ItemTypes.LOAD) {
            deleteLoad(id)
          } else if (type === ItemTypes.TRUCK) {
            deleteTruck(id)
          }

          if (openedTransaction) {
            refreshTransaction()
          }
          if (closeTransaction) {
            closeTransaction()
          }
          popConfirm(false)
        }
      }
    })
  }

  const key = type === ItemTypes.LOAD ? 'DELETE_LOAD' : 'DELETE_TRUCK'
  const fallBack = (
    <GHref
      tip={t(transaction?.disabledActionTooltips && typeof transaction?.disabledActionTooltips[key] !== 'undefined'
        ? 'tableRow:' + transaction.disabledActionTooltips[key]
        : 'tableRow:noPermission')}
      className="p-0 d-flex col flex-center cell-icon disabled">
      <WrappedComponent transaction={transaction} />
    </GHref>
  )

  let requiredPermissions = isCancelButton ? cancelPermissions : deletePermissions[board]

  if (type === ItemTypes.LOAD && !transaction.matchedLoad?.internalOwner && isBroker()) {
    requiredPermissions = EXTERNAL_LOAD_DELETE
  }

  if ((type === ItemTypes.LOAD && transaction?.disabledActions?.includes('DELETE_LOAD'))
      || (type === ItemTypes.TRUCK && transaction?.disabledActions?.includes('DELETE_TRUCK'))) {
    return fallBack
  }

  return (
    transaction?.enabledOperations?.delete ? (
      <RequiresPermission
        requiredPermissions={transaction?.isMine ? [] : requiredPermissions}
        fallbackComponent={fallBack}
      >
        <>
          <GHref
            onClick={deleteItem}
            className="p-0 d-flex col flex-center cell-icon"
          >
            <WrappedComponent transaction={transaction} />
          </GHref>
        </>
      </RequiresPermission>
    ) : fallBack
  )
}
